import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field, ImageUpload, SkeletonTable } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { ArchiveDialog } from "@/components/ArchiveDialog";
import { RowActions } from "@/components/RowActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Printer, Wallet, Search } from "lucide-react";
import { toast } from "sonner";
import { maybeSuggestStatus } from "@/lib/statusSuggest";

const EMPTY = { order_id: "", sender_name: "", amount: "", method: "Bank Transfer", bank_account: "", reference: "", receipt: null, notes: "", is_refund: false };

export default function Payments() {
  const { constants, trips, selectedTripId } = useApp();
  const { isAdmin } = useAuth();
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [form, setForm] = useState(EMPTY);
  // search + filters
  const [search, setSearch] = useState("");
  const [fMethod, setFMethod] = useState("");
  const [fDate, setFDate] = useState("");
  const [fStaff, setFStaff] = useState("");
  const [fCustomer, setFCustomer] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { trip_id: selectedTripId, search: search || undefined,
        method: fMethod || undefined, date: fDate || undefined, staff: fStaff || undefined, customer_id: fCustomer || undefined,
        include_deleted: isAdmin && showDeleted, page: 1, page_size: PAGE_SIZE };
      const [p, o, c] = await Promise.all([
        api.get("/payments", { params }),
        api.get("/orders", { params: { trip_id: selectedTripId } }),
        api.get("/customers"),
      ]);
      setPayments(p.data.items); setTotal(p.data.total); setHasMore(p.data.has_more); setPage(1);
      setOrders(o.data); setCustomers(c.data);
      setStaffList([...new Set(p.data.items.map((x) => x.recorded_by).filter(Boolean))]);
    } finally { setLoading(false); }
  }, [selectedTripId, search, fMethod, fDate, fStaff, fCustomer, isAdmin, showDeleted]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const p = await api.get("/payments", { params: { trip_id: selectedTripId, search: search || undefined,
        method: fMethod || undefined, date: fDate || undefined, staff: fStaff || undefined, customer_id: fCustomer || undefined,
        include_deleted: isAdmin && showDeleted, page: next, page_size: PAGE_SIZE } });
      setPayments((prev) => [...prev, ...p.data.items]); setHasMore(p.data.has_more); setPage(next);
    } finally { setLoadingMore(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setOpen(true); };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") { openNew(); searchParams.delete("new"); setSearchParams(searchParams, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const selectedOrder = orders.find((o) => o.id === form.order_id);

  const save = async () => {
    if (!form.order_id) return toast.error("Pilih order");
    if (!form.sender_name || !form.sender_name.trim()) return toast.error("Nama Pengirim wajib diisi");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Jumlah tidak valid");
    try { const res = await api.post("/payments", form); toast.success("Pembayaran dicatat"); setOpen(false); load(); maybeSuggestStatus(res.data, load); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const showReceipt = async (p) => { const r = await api.get(`/payments/${p.id}/receipt`); setReceipt(r.data); };
  const resetFilters = () => { setSearch(""); setFMethod(""); setFDate(""); setFStaff(""); setFCustomer(""); };

  const doArchive = async () => {
    await api.delete(`/payments/${archiveTarget.id}`);
    toast.success("Pembayaran diarsipkan"); load();
  };

  return (
    <div>
      <PageHeader title="Pembayaran" subtitle="Catat dan kelola pembayaran pelanggan">
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground mr-1 cursor-pointer" data-testid="payment-show-deleted">
            <Checkbox checked={showDeleted} onCheckedChange={setShowDeleted} />Tampilkan Terhapus
          </label>
        )}
        <Button onClick={openNew} data-testid="payment-create-button"><Plus className="h-4 w-4 mr-1.5" />Catat Pembayaran</Button>
      </PageHeader>

      {/* Search + filters */}
      <div className="flex flex-col gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cari no order, nama pengirim, metode, referensi..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="payments-search-input" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Select value={fMethod || "all"} onValueChange={(v) => setFMethod(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="payments-method-filter"><SelectValue placeholder="Metode" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Metode</SelectItem>{(constants?.payment_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <SearchableSelect
            value={fCustomer || "all"}
            onChange={(v) => setFCustomer(v === "all" ? "" : v)}
            options={[{ value: "all", label: "Semua Pelanggan", keywords: ["semua"] },
              ...customers.map((c) => ({ value: c.id, label: c.name, sublabel: [c.whatsapp, c.instagram].filter(Boolean).join(" · "), keywords: [c.name, c.whatsapp, c.instagram, c.email] }))]}
            placeholder="Pelanggan"
            searchPlaceholder="Cari pelanggan..."
            emptyText="Pelanggan tidak ditemukan"
            testId="payments-customer-filter"
          />
          <Select value={fStaff || "all"} onValueChange={(v) => setFStaff(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="payments-staff-filter"><SelectValue placeholder="Staff" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Staff</SelectItem>{staffList.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} data-testid="payments-date-filter" />
        </div>
        {(search || fMethod || fDate || fStaff || fCustomer) && <button onClick={resetFilters} className="text-xs text-primary self-start" data-testid="payments-reset-filters">Reset filter</button>}
      </div>

      {loading ? <SkeletonTable /> : payments.length === 0 ? (
        <EmptyState title="Belum ada pembayaran" action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Catat Pembayaran</Button>} />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {payments.map((p) => (
            <div key={p.id} className={`flex items-center gap-3 p-3.5 border-b border-border last:border-0 ${p.deleted ? "opacity-60 bg-secondary/30" : ""}`} data-testid="payment-row">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${p.is_refund ? "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]" : "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]"}`}><Wallet className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{p.order_number} · {p.method}{p.deleted && <span className="ml-2 text-xs text-destructive">(Terarsip)</span>}</p>
                <p className="text-sm text-foreground">{p.sender_name || <span className="italic text-muted-foreground">Nama pengirim belum dicatat</span>}</p>
                <p className="text-xs text-muted-foreground">{formatDate(p.payment_date)} · Dicatat oleh {p.recorded_by}</p>
              </div>
              <p className={`font-mono font-semibold ${p.is_refund ? "text-destructive" : ""}`}>{p.is_refund ? "-" : ""}{formatIDR(p.amount_idr)}</p>
              <Button size="sm" variant="outline" onClick={() => showReceipt(p)} data-testid="payment-receipt-button"><Printer className="h-3.5 w-3.5" /></Button>
              {isAdmin && !p.deleted && <RowActions onArchive={() => setArchiveTarget(p)} testId="payment" />}
            </div>
          ))}
          {hasMore && (
            <div className="p-3 flex justify-center border-t border-border">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="payments-load-more">
                {loadingMore ? "Memuat…" : "Muat lebih banyak"}
              </Button>
            </div>
          )}
        </div>
      )}
      {!loading && payments.length > 0 && <p className="text-xs text-muted-foreground mt-2" data-testid="payments-count">Menampilkan {payments.length} dari {total} pembayaran</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Catat Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Order" required>
            <SearchableSelect
              value={form.order_id}
              onChange={(v) => set("order_id", v)}
              options={orders.map((o) => ({
                value: o.id,
                label: `${o.order_number} · ${o.customer_name}`,
                sublabel: `Sisa ${formatIDR(o.balance)}`,
                keywords: [o.order_number, o.customer_name],
              }))}
              placeholder="Pilih order"
              searchPlaceholder="Cari no order / nama pelanggan..."
              emptyText="Order tidak ditemukan"
              testId="payment-order-select"
            />
          </Field>
            {selectedOrder && <div className="text-sm bg-secondary/40 rounded-lg p-3 flex justify-between"><span>Total: <b>{formatIDR(selectedOrder.order_total)}</b></span><span>Sisa: <b className="text-destructive">{formatIDR(selectedOrder.balance)}</b></span></div>}
            <Field label="Nama Pengirim" required hint="Nama pada transfer bank / QRIS / e-wallet / tunai">
              <Input value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} placeholder="cth: Budi Santoso" data-testid="payment-sender-input" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Jumlah (Rp)" required><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="payment-amount-input" /></Field>
              <Field label="Metode">
                <Select value={form.method} onValueChange={(v) => set("method", v)}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(constants?.payment_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Rekening/Bank"><Input value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} /></Field>
              <Field label="No. Referensi"><Input value={form.reference} onChange={(e) => set("reference", e.target.value)} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_refund} onChange={(e) => set("is_refund", e.target.checked)} data-testid="payment-refund" />Ini adalah refund</label>
            <ImageUpload value={form.receipt} onChange={(b) => set("receipt", b)} label="Bukti" testId="payment-receipt-upload" />
            <Field label="Catatan"><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="payment-save-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-sm">
          {receipt && (<div id="receipt-print">
            <DialogHeader><DialogTitle>Kuitansi Pembayaran</DialogTitle></DialogHeader>
            <div className="space-y-2 mt-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Nomor Pesanan</span><span className="font-medium">{receipt.order_number}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Nama Pelanggan</span><span>{receipt.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Nama Pengirim</span><span data-testid="receipt-sender">{receipt.sender_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Metode Pembayaran</span><span>{receipt.payment.method}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tanggal</span><span>{formatDate(receipt.payment.payment_date)}</span></div>
              <div className="flex justify-between border-t border-border pt-2"><span>Jumlah Dibayar</span><span className="font-heading text-xl font-semibold text-primary">{receipt.amount_formatted}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Order</span><span className="font-mono">{formatIDR(receipt.order_total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Sisa</span><span className="font-mono">{formatIDR(receipt.balance)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Dicatat oleh</span><span>{receipt.payment.recorded_by}</span></div>
            </div>
            <DialogFooter className="no-print mt-4"><Button onClick={() => window.print()} data-testid="receipt-print-button"><Printer className="h-4 w-4 mr-1.5" />Cetak</Button></DialogFooter>
          </div>)}
        </DialogContent>
      </Dialog>

      <ArchiveDialog open={!!archiveTarget} onOpenChange={(v) => { if (!v) setArchiveTarget(null); }}
        title="Arsipkan Pembayaran?"
        description={`Pembayaran ${archiveTarget?.order_number || ""} sebesar ${formatIDR(archiveTarget?.amount_idr || 0)} akan diarsipkan. Saldo order akan dihitung ulang. Dapat dipulihkan dari Kotak Sampah.`}
        onConfirm={doArchive} testId="payment-archive" />
    </div>
  );
}
